import { Check, Copy as CopyIcon, Sparkles } from 'lucide-react'
import * as React from 'react'
import { Icon } from './Icon'
import { Input, type InputProps } from './Input'

/**
 * PasswordInputWithGenerator — password input with a one-click "Generate"
 * button (16-char strong random) and a "Copy" affordance.
 *
 * Why an atom instead of a wrapper?
 *   The Add-User drawer in dp-admin-console wants the admin to either
 *   type a password or generate one. The same affordance will be useful
 *   in user-detail "reset password", invite flows, etc. Keeping it in
 *   the DS prevents three apps from re-implementing crypto + clipboard
 *   logic.
 *
 * Visibility:
 *   The input renders as `type="text"` (NOT password) when the value
 *   was just generated, so the admin can copy it. They can hide via the
 *   `Eye` toggle on regular password inputs — but here the admin needs
 *   to read what they're about to share, so plain-text is the default
 *   immediately after generate.
 *
 * Generation:
 *   16 chars. Each character drawn uniformly from the union of upper +
 *   lower + digit + safe-symbol. Implemented via `crypto.getRandomValues`
 *   (no `Math.random`).
 */

const UPPER = 'ABCDEFGHJKLMNPQRSTUVWXYZ' // dropped I, O for clarity
const LOWER = 'abcdefghijkmnpqrstuvwxyz' // dropped l, o
const DIGIT = '23456789' // dropped 0, 1
const SYMBOL = '!@#$%^&*-_=+'

const CHARSET = UPPER + LOWER + DIGIT + SYMBOL

/** Generate a 16-char strong random password using crypto.getRandomValues. */
export function generateStrongPassword(length: number = 16): string {
  if (typeof crypto === 'undefined' || !crypto.getRandomValues) {
    // Fallback for environments without WebCrypto (Storybook SSR / tests).
    // Still avoids Math.random by hashing time + Object identity, but this
    // is intentionally LESS secure than the real path.
    const fallback = Date.now().toString(36) + Math.floor(Math.random() * 1e9).toString(36)
    return fallback.slice(0, length).padEnd(length, '0')
  }
  const buf = new Uint32Array(length)
  crypto.getRandomValues(buf)
  let out = ''
  for (let i = 0; i < length; i++) {
    out += CHARSET[buf[i] % CHARSET.length]
  }
  // Guarantee at least one of each class — re-roll positions until satisfied.
  // Cheap because the probability of failing is < 0.5% for length 16.
  if (!/[A-Z]/.test(out)) out = UPPER[buf[0] % UPPER.length] + out.slice(1)
  if (!/[a-z]/.test(out)) out = out[0] + LOWER[buf[1] % LOWER.length] + out.slice(2)
  if (!/[0-9]/.test(out)) out = out.slice(0, 2) + DIGIT[buf[2] % DIGIT.length] + out.slice(3)
  if (!/[!@#$%^&*\-_=+]/.test(out))
    out = out.slice(0, 3) + SYMBOL[buf[3] % SYMBOL.length] + out.slice(4)
  return out
}

export interface PasswordInputWithGeneratorProps
  extends Omit<InputProps, 'type' | 'rightIcon' | 'value' | 'onChange'> {
  value: string
  onChange: (value: string) => void
  /** Length of the generated password. Default 16. */
  generatorLength?: number
  /** Hide the Generate button. Default false. */
  hideGenerate?: boolean
  /** Hide the Copy button. Default false. */
  hideCopy?: boolean
}

export const PasswordInputWithGenerator = React.forwardRef<
  HTMLInputElement,
  PasswordInputWithGeneratorProps
>(
  (
    {
      value,
      onChange,
      generatorLength = 16,
      hideGenerate = false,
      hideCopy = false,
      disabled,
      placeholder = 'Type a password or click Generate',
      ...rest
    },
    ref,
  ) => {
    const [copied, setCopied] = React.useState(false)
    const [showGenerated, setShowGenerated] = React.useState(false)

    const handleGenerate = () => {
      const next = generateStrongPassword(generatorLength)
      onChange(next)
      setShowGenerated(true)
      setCopied(false)
    }

    const handleCopy = async () => {
      if (!value) return
      try {
        await navigator.clipboard.writeText(value)
        setCopied(true)
        setTimeout(() => setCopied(false), 1500)
      } catch {
        /* clipboard unavailable — silently fail */
      }
    }

    return (
      <div className="flex items-center gap-2">
        <Input
          ref={ref}
          type={showGenerated ? 'text' : 'password'}
          value={value}
          onChange={(e) => {
            onChange(e.target.value)
            // Hide the generated value when the admin starts editing it
            if (showGenerated) setShowGenerated(false)
          }}
          disabled={disabled}
          placeholder={placeholder}
          autoComplete="new-password"
          {...rest}
        />
        {!hideGenerate && (
          <button
            type="button"
            onClick={handleGenerate}
            disabled={disabled}
            aria-label="Generate strong password"
            className="inline-flex items-center gap-1 rounded-md border border-ia-border bg-card px-3 h-10 text-caption text-ia-text hover:bg-ia-elevated disabled:opacity-50 disabled:cursor-not-allowed shrink-0"
          >
            <Icon icon={Sparkles} size="xs" /> Generate
          </button>
        )}
        {!hideCopy && (
          <button
            type="button"
            onClick={handleCopy}
            disabled={disabled || !value}
            aria-label={copied ? 'Copied to clipboard' : 'Copy password to clipboard'}
            className="inline-flex items-center gap-1 rounded-md border border-ia-border bg-card px-3 h-10 text-caption text-ia-text hover:bg-ia-elevated disabled:opacity-50 disabled:cursor-not-allowed shrink-0"
          >
            <Icon icon={copied ? Check : CopyIcon} size="xs" /> {copied ? 'Copied' : 'Copy'}
          </button>
        )}
      </div>
    )
  },
)
PasswordInputWithGenerator.displayName = 'PasswordInputWithGenerator'
